<template>
  <div class="checkout-contact">
    <h3 class="checkout-contact__title">Контактные данные</h3>
    <checkout-auth v-show="!LOGGED_IN" />
    <div class="checkout-contact-form">
      <div>
        <ui-input
          label="Имя *"
          placeholder="Имя"
          ref="name"
          :validate="['name']"
          v-model="name"
          clearable
          @blur="saveForm"
        />
      </div>
      <div>
        <ui-input
          label="Телефон *"
          ref="phone"
          placeholder="Телефон"
          mask="phone"
          type="tel"
          validate="phone"
          v-model="phone"
          :error="phoneError"
          clearable
          @on-valid="handlePhone"
          @is-valid="togglePhoneValid"
        >
          <template v-if="isPhoneExists" #error>
            <exists-error type="tel" @login="onPhoneLogin" />
          </template>
        </ui-input>
        <vue-client-only>
          <div
            v-if="phoneValid && confirmRequired && !phoneError && !codeSent && !retryTimeoutSeconds"
            class="checkout-contact__code-hint"
          >
            На указанный номер будет отправлен код подтверждения
          </div>
          <div
            v-if="phoneValid && confirmRequired && !phoneError && codeSent && retryTimeoutSeconds > 0"
            class="checkout-contact__code-hint"
          >
            На указанный номер отправлен код подтверждения
          </div>
          <template v-if="confirmRequired">
            <template v-if="phoneValid && !codeSent && !isPhoneExists">
              <div>
                <p class="countdown" v-if="retryTimeoutSeconds > 0">
                  Повторная отправка возможна через {{ retryTimeout }}
                </p>
              </div>
              <ui-button
                class="checkout-contact__phone-submit"
                wide
                text="Получить код"
                :disabled="(retryTimeoutSeconds > 0 && confirmedPhones.length > 0)"
                @click="requestCode"
                :loading="codeProgress"
                v-if="!retryTimeoutSeconds"
              />
            </template>
            <template v-if="phoneValid && codeSent">
              <ui-input
                @is-valid="onCodeValid"
                @input="codeError = ''"
                :error="codeError"
                ref="code"
                v-model="code"
                name="code"
                mask="code"
                label="Код *"
                validate="code"
                clearable
                numbers
                type="tel"
                placeholder="Введите код из SMS"
              />
              <p
                v-if="!retryTimeoutSeconds && codeSent"
                @click="requestCode"
                class="countdown resend"
              >
                Отправить код повторно
              </p>
              <div>
                <p class="countdown" v-if="retryTimeoutSeconds > 0">
                  Повторная отправка возможна через {{ retryTimeout }}
                </p>
              </div>
              <ui-button
                v-if="codeSent"
                class="checkout-contact__code-submit"
                wide
                text="Подтвердить"
                :disabled="!isCodeValid"
                @click="verifyCode"
                :loading="codeSubmitProgres"
              />
            </template>
          </template>
        </vue-client-only>
      </div>
      <div>
        <ui-input
          label="Email *"
          placeholder="Email"
          ref="email"
          validate="email"
          v-model="email"
          :error="emailError"
          clearable
          :disabled="!!ACCOUNT.email"
          @blur="saveForm"
          @on-valid="handleEmail"
          @is-valid="onEmailValidChange"
        >
          <template v-if="isEmailExists" #error>
            <exists-error type="email" @login="onEmailLogin" />
          </template>
        </ui-input>
        <ui-checkbox
          class="checkout-contact__subscribe"
          v-if="showSubscribe"
          v-model="subscribe"
          label="Подписаться на акции и скидки"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';
import { mapActions, mapGetters } from 'vuex';
import CheckoutAuth from '@/components/checkout/checkout-auth.vue';
import ExistsError from '@/components/checkout/checkout-contact/exists-error.vue';
import { AUTH_TYPES } from '@/constants/auth';

export default {
  name: 'checkout-contact',

  components: {
    VueClientOnly,
    ExistsError,
    CheckoutAuth,
  },

  data: () => ({
    name: '',
    phone: '',
    isPhoneExists: false,
    code: '',
    isCodeValid: false,
    codeError: '',
    codeSubmitProgres: false,
    codeProgress: false,
    codeSent: false,
    retryTimeoutSeconds: 0,
    token: '',
    phoneError: '',
    isEmailInputValid: false,
    email: '',
    emailError: '',
    confirmedPhones: [],
    phoneValid: false,
    isEmailExists: false,
  }),

  computed: {
    ...mapGetters({
      LOGGED_IN: 'user/loggedIn',
      ACCOUNT: 'user/account',
      SUBSCRIPTIONS: 'user/subscriptions',
      IS_EMAIL_CONFIRMED: 'user/isEmailConfirmed',
      CONTACT: 'cart/contact',
      SHIPPING_GROUPS: 'cart/shippingGroups',
    }),
    confirmRequired() {
      const phones = [this.ACCOUNT.phone, this.CONTACT.phone, ...this.confirmedPhones];

      return (!phones.includes(this.phone));
    },
    retryTimeout() {
      const minutes = Math.floor(this.retryTimeoutSeconds / 60);
      const seconds = this.retryTimeoutSeconds - minutes * 60;
      return `${minutes ? minutes + ' мин' : ''} ${seconds} с`;
    },
    showSubscribe() {
      if (!this.email || !this.isEmailInputValid) {
        return false;
      }

      if (!this.IS_EMAIL_CONFIRMED && this.SUBSCRIPTIONS.some(s => s.active)) {
        return false;
      }

      if (this.IS_EMAIL_CONFIRMED && this.SUBSCRIPTIONS.some(s => s.code === 'EMAIL' && s.active)) {
        return false;
      }

      return true;
    },
    subscribe: {
      get() {
        return this.$store.state.cart.subscribe;
      },
      set(v) {
        this.$store.commit('cart/subscribe', v);
      }
    },
  },
  watch: {
    phone() {
      this.phoneError = '';
      this.codeSent = false;
      this.codeError = '';
    },

    email() {
      this.emailError = '';
    },

    LOGGED_IN() {
      if (this.phoneError) this.phoneError = '';
      if (this.emailError) this.emailError = '';
      this.isEmailExists = false;
      this.isPhoneExists = false;
    }
  },

  mounted() {
    this.setForm();
    this.$f.$on('login', this.onLogin);
    this.subscribe = true;
    if (this.$refs?.email?.checkValid) {
      this.isEmailInputValid = this.$refs.email.checkValid();
    }
  },

  methods: {
    ...mapActions({
      SAVE_CONTACT: 'cart/saveContact',
      CREATE_NOTIFICATION: 'notifications/create',
    }),
    onLogin() {
      this.setForm();
      if (this.confirmedPhones.includes(this.phone)) {
        this.saveForm();
      }
    },
    onEmailLogin() {
      this.$f.login({
        email: this.email,
        mode: AUTH_TYPES.email,
      });
    },
    onPhoneLogin() {
      this.$f.login({
        phone: this.phone,
        mode: AUTH_TYPES.sms,
      });
    },
    async checkExistsPhone() {
      try {
        const { exists } = await this.$store.$api.accountExists({ value: this.phone });

        this.isPhoneExists = exists;
        this.phoneError = exists;

        return exists;
      } catch (error) {
        console.log(error);
      }
    },
    async checkEmailExists() {
      try {
        const { exists } = await this.$store.$api.accountExists({
          value: this.email,
          type: 'email',
          scanUnconfirmed: true,
        });

        this.isEmailExists = exists;
        this.emailError = exists;

        return exists;
      } catch (error) {
        console.log(error);
      }
    },
    requestCode() {
      this.code = '';
      this.codeSent = false;
      this.codeProgress = true;

      this.$f.requestCode({ request: { phone: this.phone } })
        .then(r => {
          this.codeProgress = false;
          this.codeSent = true;
          this.retryTimeoutSeconds = r.retryTimeoutSeconds;
          this.retryCountdown();
        })
        .catch(({ error = {} } = {}) => {
          this.codeProgress = false;
          if (error.code) {
            this.CREATE_NOTIFICATION({
              message: error.detail,
            });
          }
        })
    },
    verifyCode() {
      this.codeSubmitProgres = true;
      this.$store.$api.verifyCode({ phone: this.phone, code: this.code })
        .then(r => {
          this.code = '';
          this.phoneError = '';
          this.token = r.token;
          this.saveForm();
          this.confirmedPhones.push(this.phone);
        })
        .catch(({ error = {} } = {}) => {
          this.codeError = error.detail || 'Введён неверный код подтверждения';
        })
        .finally(() => {
          this.codeSubmitProgres = false;
        });
    },
    retryCountdown() {
      this.retryTimeoutSeconds--;
      if (this.retryTimeoutSeconds <= 0) {
        this.retryTimeoutSeconds = 0;
        return;
      }
      setTimeout(this.retryCountdown, 1000);
    },

    handlePhone() {
      if (!this.LOGGED_IN && this.confirmRequired) {
        this.checkExistsPhone();
      }
    },

    handleEmail() {
      if (!this.LOGGED_IN && this.$refs.email.valid) {
        this.checkEmailExists();
      }
    },

    togglePhoneValid(isValid) {
      this.phoneValid = isValid;
      if (!isValid) this.isPhoneExists = false;
    },

    async isPhoneValid() {
      const phoneInput = this.$refs?.phone;
      if (!phoneInput) {
        throw new Error('Инпут телефона не найден');
      }

      const isInputValid = phoneInput.checkValidOutside();
      this.phoneValid = isInputValid;
      if (this.confirmRequired && this.phoneValid) {
        this.phoneError = 'Подтвердите номер телефона';
      }

      if (this.confirmRequired) {
        const isPhoneNotExists = await this.checkExistsPhone() === false;

        return isInputValid && !this.confirmRequired && isPhoneNotExists;
      }

      return isInputValid && !this.confirmRequired;
    },

    async isEmailValid() {
      const emailInput = this.$refs?.email;

      if (!emailInput) {
        throw new Error('Инпут email не найден');
      }

      if (this.LOGGED_IN) {
        return emailInput.checkValidOutside();
      }

      const isInputValid = emailInput.checkValidOutside();
      const isExists = await this.checkEmailExists() === false;

      return isExists && isInputValid;
    },
    async checkFormValidity() {
      const { name } = this.$refs;
      const isValid = [
        name.checkValidOutside(),
        await this.isPhoneValid(),
        await this.isEmailValid(),
      ].every(isValid => isValid === true);

      return Promise.resolve(isValid);
    },
    saveForm() {
      if (
        this.email && !this.$refs.email.valid
        || this.name && !this.$refs.name.valid
      ) return;

      // нужен, чтобы получить актуальное значение email,
      // т.к blur срабатывает раньше input при очистке
      setTimeout(() => {
        if (!this.LOGGED_IN || !this.email || this.ACCOUNT.email) return;
        this.CREATE_NOTIFICATION({
          type: 'warning',
          title: 'Добавьте Email в профиль',
          message: 'Чтобы быстрее оформлять заказы =)',
        });
      }, 0);

      const payload = {
        contact: {
          fullName: this.name,
          email: this.email,
        },
      };
      if (this.token) {
        payload.contact.phone = this.phone;
        payload.token = this.token;
      }
      this.SAVE_CONTACT(payload)
        .then(() => { this.token = '' })
        .catch(({ error = { code: '' } } = {}) => {
          if (error.code.includes('.email')) {
            this.emailError = error.title || '';
          }
        });
    },
    setForm() {
      this.name = this.name || this.CONTACT.fullName || this.ACCOUNT.firstName;
      this.phone = this.CONTACT.phone || this.ACCOUNT.phone;
      this.email = this.CONTACT.email || this.ACCOUNT.email;
    },
    onCodeValid(val) {
      this.isCodeValid = val;
    },
    onEmailValidChange(isValid) {
      this.isEmailInputValid = isValid;
      if (!isValid) this.isEmailExists = false;
    }
  },
}
</script>

<style lang="scss">
.checkout-contact {
  h3.checkout-contact__title {
    margin-bottom: 20px;
  }

  &-form {
    display: flex;
    flex-direction: column;
    > * {
      margin: 0;
    }
  }
  p.countdown {
    font-size: 12px;
    color: $gray;
    margin-top: 4px;
    margin-bottom: 16px;
    &.resend {
      display: inline-block;
      cursor: pointer;
      color: $blue;
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &__code-hint {
    color: $gray;
    font-size: 12px;
    line-height: 16px;
    margin-top: -16px;
    margin-bottom: 16px;
  }

  &__code-block {
    margin-bottom: 16px;
  }

  & &__code-submit,
  & &__phone-submit {
    margin-bottom: 12px;
  }

  & &__code-submit {
    margin-top: 16px;
  }
}

@include breakpoint("desktop") {
  .checkout-contact {
    &-form {
      flex-direction: row;
      flex-wrap: wrap;

      > * {
        flex: 1 1 26%;

        &:not(:last-child) {
          margin-right: 24px;
        }
      }

      .ui-textarea {
        flex: 1 1 100%;
        margin-top: 0;
      }
    }
  }
}
</style>
